import type { Reward, Artist } from "@/mocks/artistData";

interface Props {
  reward: Reward;
  artist: Artist;
  myXP: number;
  onSelect: () => void;
}

const badgeStyle: Record<string, string> = {
  LEGENDARY: "bg-[#ffd60a]/20 text-[#ffd60a] border-[#ffd60a]/50",
  "ULTRA RARE": "bg-[#f72585]/20 text-[#f72585] border-[#f72585]/50",
  RARE: "bg-[#4fc3f7]/20 text-[#4fc3f7] border-[#4fc3f7]/50",
};

export default function ArtistRewardCard({ reward, artist, myXP, onSelect }: Props) {
  const canRedeem = myXP >= reward.xpCost;
  const isSoldOut = reward.stock === 0;
  const stockPercent = Math.round((reward.stock / reward.totalStock) * 100);
  const xpNeeded = reward.xpCost - myXP;

  return (
    <button
      onClick={onSelect}
      className={`relative rounded-2xl border bg-[#12121a] overflow-hidden text-left group transition-all duration-300 hover:scale-[1.02] cursor-pointer w-full ${
        canRedeem && !isSoldOut
          ? "hover:border-opacity-80"
          : "border-white/10 hover:border-white/20"
      }`}
      style={canRedeem && !isSoldOut ? { borderColor: `${artist.accentColor}60` } : {}}
    >
      {/* Thumbnail */}
      <div className="relative overflow-hidden" style={{ aspectRatio: "16/9" }}>
        <img
          src={reward.image}
          alt={reward.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#12121a]/90 via-transparent to-transparent" />

        {/* Badges */}
        <div className="absolute top-3 left-3 flex gap-2 flex-wrap">
          {reward.badge && (
            <span className={`px-2.5 py-1 rounded-full text-[10px] font-black border ${badgeStyle[reward.badge] ?? ""}`}>
              {reward.badge}
            </span>
          )}
          {isSoldOut && (
            <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-white/10 text-white/50 border border-white/20">
              SOLD OUT
            </span>
          )}
        </div>

        {/* Category Label */}
        <div className="absolute bottom-3 left-3">
          <span
            className="px-2.5 py-1 rounded-full text-[10px] font-semibold"
            style={{ background: `${reward.color}20`, color: reward.color, border: `1px solid ${reward.color}40` }}
          >
            <i className={`${reward.icon} mr-1`} />
            {reward.categoryLabel}
          </span>
        </div>

        {/* Can Redeem Indicator */}
        {canRedeem && !isSoldOut && (
          <div
            className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center rounded-full"
            style={{ background: `${artist.accentColor}30`, border: `1px solid ${artist.accentColor}60` }}
          >
            <i className="ri-check-line text-xs" style={{ color: artist.accentColor }} />
          </div>
        )}
      </div>

      {/* Body */}
      <div className="p-5">
        <h3 className="text-white font-bold text-sm mb-2 line-clamp-2 leading-snug">{reward.title}</h3>
        <p className="text-white/40 text-xs line-clamp-2 mb-4 leading-relaxed">{reward.description}</p>

        {/* Perks */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {reward.perks.slice(0, 3).map((perk, i) => (
            <span key={i} className="px-2 py-0.5 rounded-md text-[10px] bg-white/5 text-white/40 border border-white/10">
              {perk}
            </span>
          ))}
          {reward.perks.length > 3 && (
            <span className="px-2 py-0.5 rounded-md text-[10px] bg-white/5 text-white/30 border border-white/10">
              +{reward.perks.length - 3}
            </span>
          )}
        </div>

        {/* Stock Bar */}
        <div className="mb-4">
          <div className="flex justify-between items-center mb-1.5">
            <span className="text-white/30 text-[10px]">잔여 수량</span>
            <span className="text-white/50 text-[10px] font-semibold">
              {reward.stock.toLocaleString()} / {reward.totalStock.toLocaleString()}
            </span>
          </div>
          <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-700"
              style={{
                width: `${stockPercent}%`,
                background: stockPercent > 30 ? artist.accentColor : "#f72585",
              }}
            />
          </div>
        </div>

        {/* XP Cost & Status */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <i className="ri-coin-fill text-sm" style={{ color: artist.accentColor }} />
            <span className="font-black text-base" style={{ color: artist.accentColor }}>
              {reward.xpCost.toLocaleString()}
            </span>
            <span className="text-white/30 text-xs">{artist.name} XP</span>
          </div>
          <span
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${
              isSoldOut
                ? "bg-white/5 text-white/30"
                : canRedeem
                ? "border"
                : "bg-white/5 text-white/40 border border-white/10"
            }`}
            style={canRedeem && !isSoldOut ? {
              background: `${artist.accentColor}20`,
              color: artist.accentColor,
              borderColor: `${artist.accentColor}40`,
            } : {}}
          >
            {isSoldOut ? "품절" : canRedeem ? "교환 가능" : `${xpNeeded.toLocaleString()} XP 부족`}
          </span>
        </div>

        {/* Deadline */}
        {reward.deadline && !isSoldOut && (
          <div className="mt-3 flex items-center gap-1.5 text-white/30 text-[10px]">
            <i className="ri-time-line" />
            <span>마감: {reward.deadline}</span>
          </div>
        )}
      </div>
    </button>
  );
}
